import React from 'react';
import { Users, Code, Briefcase, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Services = () => {
  const { t } = useTranslation();

  const serviceCategories = [
    {
      title: t('services.categories.it.title', "Information Technology"),
      icon: Code,
      description: t('services.categories.it.description', "Expert placement services for IT professionals"),
      positions: [
        t('services.categories.it.positions.softwareDev', "Software Developers"),
        t('services.categories.it.positions.sysAdmin', "System Administrators"),
        t('services.categories.it.positions.networkEng', "Network Engineers"),
        t('services.categories.it.positions.dataAnalyst', "Data Analysts"),
        t('services.categories.it.positions.security', "Cybersecurity Specialists"),
        t('services.categories.it.positions.dataAdmin', "Data Administrators"),
        t('services.categories.it.positions.projectManager', "IT Project Managers"),
        t('services.categories.it.positions.webDev', "Web Developers"),
        t('services.categories.it.positions.cloudArch', "Cloud Solutions Architects"),
        t('services.categories.it.positions.helpDesk', "Help Desk Technicians")
      ]
    },
    {
      title: t('services.categories.engineering.title', "Engineering"),
      icon: Briefcase,
      description: t('services.categories.engineering.description', "Connecting top engineering talent with leading organizations"),
      positions: [
        t('services.categories.engineering.positions.mechanical', "Mechanical Engineer"),
        t('services.categories.engineering.positions.civil', "Civil Engineer"),
        t('services.categories.engineering.positions.electrical', "Electrical Engineer"),
        t('services.categories.engineering.positions.software', "Software Engineer"),
        t('services.categories.engineering.positions.aerospace', "Aerospace Engineer"),
        t('services.categories.engineering.positions.industrial', "Industrial Engineer"),
        t('services.categories.engineering.positions.environmental', "Environmental Engineer"),
        t('services.categories.engineering.positions.manufacturing', "Manufacturing Engineer"),
        t('services.categories.engineering.positions.system', "System Engineer"),
        t('services.categories.engineering.positions.data', "Data Engineer"),
        t('services.categories.engineering.positions.robotics', "Robotics Engineer")
      ]
    },
    {
      title: t('services.categories.administrative.title', "Administrative"),
      icon: FileText,
      description: t('services.categories.administrative.description', "Comprehensive administrative staffing solutions"),
      positions: [
        t('services.categories.administrative.positions.officeManager', "Office Manager"),
        t('services.categories.administrative.positions.execAssistant', "Executive Assistant"),
        t('services.categories.administrative.positions.adminAssistant', "Administrative Assistant"),
        t('services.categories.administrative.positions.caseWorker', "Case Worker"),
        t('services.categories.administrative.positions.clerk', "Clerk"),
        t('services.categories.administrative.positions.customerService', "Customer Service Representative"),
        t('services.categories.administrative.positions.cashier', "Cashier"),
        t('services.categories.administrative.positions.warehouse', "Warehouse"),
        t('services.categories.administrative.positions.lightIndustrial', "Light Industrial")
      ]
    }
  ];

  return (
    <div className="bg-green-50 min-h-screen">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-green-800 mb-4">{t('services.hero.title', 'Our Services')}</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('services.hero.description', 'Since 2003, SBG has been a beacon of excellence in the recruitment industry, founded by Greg Scott and Millie Gonzalez-Scott with over 50 years of collective experience.')}
          </p>
        </div>

        {/* Company Overview */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-16 border-l-4 border-orange-500">
          <h2 className="text-3xl font-semibold text-gray-900 mb-6">{t('services.overview.title', 'About Our Expertise')}</h2>
          <div className="space-y-4 text-gray-600">
            <p>
              {t('services.overview.paragraph1', 'SBG is dedicated to serving clients in the IT, engineering, and administrative positions realms. Their shared passion for connecting talented individuals with the right opportunities has built a reputation for understanding the unique needs of both employers and candidates.')}
            </p>
            <p>
              {t('services.overview.paragraph2', 'Over the years, SBG has specialized in sourcing candidates for some of the most challenging roles in the market. Their commitment to excellence has equipped them with the tools to find and place professionals in positions that many thought were difficult or even impossible to fill.')}
            </p>
            <p>
              {t('services.overview.paragraph3', 'As SBG continues to grow and evolve, its foundational values remain unchanged. It is dedicated to providing personalized service, ensuring that every client and candidate feels valued and understood.')}
            </p>
          </div>
        </div>

        {/* Service Categories */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {serviceCategories.map((category, index) => (
            <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-all duration-300 border-b-4 border-transparent hover:border-orange-500">
              <div className="flex items-center mb-4">
                <div className="bg-orange-100 p-3 rounded-full mr-4">
                  <category.icon className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-green-800">{category.title}</h3>
              </div>
              <p className="text-gray-600 mb-4">{category.description}</p>
              <ul className="list-disc list-inside text-gray-600 space-y-2 marker:text-orange-500">
                {category.positions.map((position, idx) => (
                  <li key={idx} className="hover:text-orange-600 transition-colors duration-200">{position}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Services;
